import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import PropTypes from "prop-types";
import { useTheme } from "@mui/material/styles";

// @mui material components
import Card from "@mui/material/Card";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Grid from "@mui/material/Grid";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Icon from "@mui/material/Icon";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDBadge from "components/MDBadge";
import MDButton from "components/MDButton";
import MDAlert from "components/MDAlert";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

import DauTrang, { NhanDau } from "components/DauTrang";
import TheChiSo from "components/TheChiSo";
import XemChungTuTrongApp from "components/XemChungTuTrongApp";
import { oTieuDeCot, AN_DUOI_MD, chuXuongDong } from "lib/kieuBangDuLieu";
import {
  useDialogFullManHinh,
  oDialogContentFormDai,
  oDialogPaperFormDai,
} from "lib/dungDialogFullManHinh";
import { LAYOUT, SEMANTIC } from "assets/theme/avocadoBrand";
import { giaoDichApi, kiemTraDaChuong } from "api/giaoDich";
import { danhMucApi } from "api/danhMuc";
import { banNhapThuMuaApi } from "api/banNhapThuMua";
import { useAuth } from "context/AuthContext";
import { mauHinhThucTt } from "lib/nhanTrangThaiSot";
import { nhanTrangThaiThanhToan } from "lib/nhanTrangThaiThanhToan";
import { phatTinhTinh } from "lib/amThanhThongBao";
import useTour from "hooks/useTour";
import buocGiaoDichCuaToi from "tours/giaoDichCuaToi";

function gioPhut(iso) {
  return new Date(iso).toLocaleTimeString("vi-VN", { hour: "2-digit", minute: "2-digit" });
}

function ngayGio(iso) {
  return new Date(iso).toLocaleString("vi-VN", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function ThongTinChiTiet({ nhan, giaTri, xs }) {
  return (
    <Grid item xs={xs}>
      <MDTypography variant="caption" color="text" textTransform="uppercase">
        {nhan}
      </MDTypography>
      <MDTypography variant="button" fontWeight="medium" display="block" sx={chuXuongDong}>
        {giaTri ?? "—"}
      </MDTypography>
    </Grid>
  );
}

ThongTinChiTiet.propTypes = {
  nhan: PropTypes.string.isRequired,
  giaTri: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  xs: PropTypes.number,
};

ThongTinChiTiet.defaultProps = {
  giaTri: null,
  xs: 6,
};

// Dưới md các cột phụ của bảng nháp bị ẩn — gom lại thành một dòng nhỏ ngay
// dưới tên chủ vườn để không mất dữ liệu, xem lib/kieuBangDuLieu.
function PhuNhap({ nhap, tamTinh }) {
  const phan = [
    nhap.tenVuon,
    nhap.maSotHienThi &&
      `Sọt: ${nhap.maSotHienThi}${nhap.soSotKhac > 0 ? ` +${nhap.soSotKhac} sọt khác` : ""}`,
    `${nhap.items.length} loại bơ`,
    `${tamTinh.toLocaleString("vi-VN")} đ`,
  ].filter(Boolean);
  return (
    <MDTypography
      variant="caption"
      color="text"
      display={{ xs: "block", md: "none" }}
      mt={0.25}
      lineHeight={1.4}
    >
      {phan.join(" · ")}
    </MDTypography>
  );
}

PhuNhap.propTypes = {
  nhap: PropTypes.shape({
    tenVuon: PropTypes.string,
    maSotHienThi: PropTypes.string,
    soSotKhac: PropTypes.number,
    items: PropTypes.array,
  }).isRequired,
  tamTinh: PropTypes.number.isRequired,
};

function PhuGiaoDich({ po }) {
  const phan = [gioPhut(po.thoiGian), po.tenVuon, po.tenLoaiBoGop, `${po.tongKgPO} kg`].filter(
    Boolean
  );
  return (
    <MDTypography
      variant="caption"
      color="text"
      display={{ xs: "block", md: "none" }}
      mt={0.25}
      lineHeight={1.4}
    >
      {phan.join(" · ")}
    </MDTypography>
  );
}

PhuGiaoDich.propTypes = {
  po: PropTypes.shape({
    thoiGian: PropTypes.string,
    tenVuon: PropTypes.string,
    tenLoaiBoGop: PropTypes.string,
    tongKgPO: PropTypes.number,
  }).isRequired,
};

function GiaoDichCuaToi() {
  const { palette } = useTheme();
  const fullManHinh = useDialogFullManHinh();
  const { user } = useAuth();
  const [ngay, setNgay] = useState(() => new Date().toISOString().slice(0, 10));
  const [danhSachPO, setDanhSachPO] = useState([]);
  const [tenHinhThucTt, setTenHinhThucTt] = useState({});
  const [dangTai, setDangTai] = useState(true);
  const [xemChiTiet, setXemChiTiet] = useState(null);
  const [dangChuong, setDangChuong] = useState(false);
  const [loiChuong, setLoiChuong] = useState("");
  const [thongBaoChuong, setThongBaoChuong] = useState("");
  // PO trải >1 sọt (khi 1 sọt đầy giữa chừng lúc cân, xem
  // giaoDichApi.taoMoi/suaPO) — chỉ hiện thêm chi tiết sọt trong chi tiết
  // loại bơ lúc cần, giữ popup gọn như cũ với >95% PO chỉ dùng đúng 1 sọt.
  // Link "Quay lại sửa PO" vẫn dùng maSot top-level làm route param, không
  // đổi — sửa PO tự nạp lại đủ mọi sọt của PO đó (xem FormThuMua).
  const coNhieuSotXemChiTiet = (xemChiTiet?.danhSachSot?.length ?? 0) > 1;
  // Gom items theo LOẠI BƠ (khớp cách nhập theo khối ở FormThuMua) — mỗi
  // loại bơ 1 tiêu đề duy nhất, liệt kê gọn các sọt/lần cân bên dưới, thay vì
  // nhóm theo sọt trước khiến cùng 1 loại bơ bị lặp lại tên dưới mỗi sọt khi
  // 1 loại bơ trải nhiều sọt. Giữ thứ tự loại bơ xuất hiện lần đầu trong
  // items (không sắp lại theo tên).
  const nhomTheoLoaiBoXemChiTiet = useMemo(() => {
    if (!xemChiTiet) return null;
    const nhom = [];
    const viTri = {};
    xemChiTiet.items.forEach((it) => {
      if (!(it.maLoaiBo in viTri)) {
        viTri[it.maLoaiBo] = nhom.length;
        nhom.push({ maLoaiBo: it.maLoaiBo, tenLoaiBo: it.tenLoaiBo, items: [] });
      }
      nhom[viTri[it.maLoaiBo]].items.push(it);
    });
    return nhom;
  }, [xemChiTiet]);

  // Bản nháp (chưa lưu thành giao dịch thật, xem FormThuMua/api/banNhapThuMua)
  // — hiện riêng ở đây để tiếp tục nhập hoặc xoá mà không cần quét lại Sọt.
  const [dsNhap, setDsNhap] = useState([]);
  const [dangTaiNhap, setDangTaiNhap] = useState(true);
  const [dangXoaNhapId, setDangXoaNhapId] = useState(null);

  useEffect(() => {
    const huy = danhMucApi.langNgheHinhThucThanhToan((ds) => {
      setTenHinhThucTt(Object.fromEntries(ds.map((h) => [h.id, h.tenHinhThuc])));
    });
    return huy;
  }, []);

  useEffect(() => {
    setDangTai(true);
    const huy = giaoDichApi.langNgheCuaToi(
      ngay,
      (ds) => {
        setDanhSachPO(ds);
        setDangTai(false);
      },
      () => setDangTai(false)
    );
    return huy;
  }, [ngay]);

  // Đồng bộ popup chi tiết khi trạng thái đổi realtime (KT nhận / trả lại / đã TT).
  useEffect(() => {
    setXemChiTiet((cur) => {
      if (!cur) return cur;
      const moi = danhSachPO.find((po) => po.id === cur.id);
      return moi || cur;
    });
  }, [danhSachPO]);

  async function chuongNhacKeToan(poId) {
    setLoiChuong("");
    setThongBaoChuong("");
    const check = kiemTraDaChuong(xemChiTiet?.thoiGianChuongKeToan);
    if (!check.ok) {
      setLoiChuong(check.message);
      return;
    }
    // Chỉ kêu khi đã qua cooldown.
    phatTinhTinh("chuong");
    setDangChuong(true);
    try {
      const { thoiGianChuongKeToan } = await giaoDichApi.chuongNhacKeToan(poId);
      setXemChiTiet((cur) => (cur && cur.id === poId ? { ...cur, thoiGianChuongKeToan } : cur));
      setThongBaoChuong("Đã chuông nhắc kế toán.");
    } catch (err) {
      setLoiChuong(err.message);
    } finally {
      setDangChuong(false);
    }
  }

  useEffect(() => {
    setDangTaiNhap(true);
    if (!user?.uid) {
      setDsNhap([]);
      setDangTaiNhap(false);
      return undefined;
    }
    const huy = banNhapThuMuaApi.langNgheCuaToi(
      user.uid,
      (ds) => {
        setDsNhap(ds);
        setDangTaiNhap(false);
      },
      () => setDangTaiNhap(false)
    );
    return huy;
  }, [user?.uid]);

  async function xoaNhap(n) {
    // Xoá nháp không hoàn tác được — hỏi xác nhận 2 lần để tránh bấm nhầm.
    // eslint-disable-next-line no-alert
    if (!window.confirm("Bỏ bản nháp này? Số liệu đã nhập trước đó sẽ mất.")) return;
    // eslint-disable-next-line no-alert
    if (!window.confirm("Xác nhận lần nữa: xoá hẳn bản nháp đã lưu, không thể khôi phục lại.")) {
      return;
    }
    setDangXoaNhapId(n.id);
    try {
      await banNhapThuMuaApi.xoa(n.maChuVuon, n.maVuon);
    } finally {
      setDangXoaNhapId(null);
    }
  }

  // 1 dòng/PO (hoá đơn) đúng như thực tế thu mua — không tách theo từng loại
  // bơ nữa, vì 1 lượt thu mua nhiều loại bơ vẫn là 1 hoá đơn duy nhất. Xem
  // chi tiết từng loại bơ trong dialog khi bấm vào dòng.
  const danhSach = danhSachPO.map((po) => ({
    ...po,
    tongKgPO: po.items.reduce((s, it) => s + it.khoiLuong, 0),
    tongTienPO: po.items.reduce((s, it) => s + it.thanhTien, 0),
    tenLoaiBoGop: [...new Set(po.items.map((it) => it.tenLoaiBo).filter(Boolean))].join(", "),
  }));

  const tongKg = danhSach.reduce((s, po) => s + po.tongKgPO, 0);
  const tongTien = danhSach.reduce((s, po) => s + po.tongTienPO, 0);

  useTour("giao-dich-cua-toi", buocGiaoDichCuaToi());

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <DauTrang
          nhan="Thu mua"
          tieuDe="Giao dịch của tôi"
          moTa="Các phiếu thu mua bạn đã ghi trong ngày — bấm một dòng để xem chi tiết."
        >
          <MDBox
            id="tour-giao-dich-cua-toi-bo-loc"
            sx={{
              width: { xs: "100%", sm: LAYOUT.filterSelectWidth },
              flex: { sm: `0 0 ${LAYOUT.filterSelectWidth}px` },
            }}
          >
            <MDInput
              type="date"
              label="Ngày"
              value={ngay}
              onChange={(e) => setNgay(e.target.value)}
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
            />
          </MDBox>
        </DauTrang>

        {!dangTaiNhap && dsNhap.length > 0 && (
          <Card sx={{ mb: 3 }}>
            <MDBox px={2.5} pt={2.5} pb={1.5}>
              <NhanDau>Chưa hoàn tất</NhanDau>
              <MDTypography variant="h5" component="h2">
                Bản nháp đang dở
              </MDTypography>
              <MDTypography variant="button" fontWeight="regular" color="text">
                Chưa lưu thành giao dịch thật — tiếp tục nhập hoặc bỏ nháp.
              </MDTypography>
            </MDBox>
            <MDBox sx={{ overflowX: "auto" }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={oTieuDeCot}>Chủ vườn</TableCell>
                    <TableCell sx={[oTieuDeCot, AN_DUOI_MD]}>Vườn</TableCell>
                    <TableCell sx={[oTieuDeCot, AN_DUOI_MD]}>Sọt</TableCell>
                    <TableCell sx={[oTieuDeCot, AN_DUOI_MD]}>Lưu lúc</TableCell>
                    <TableCell align="right" sx={[oTieuDeCot, AN_DUOI_MD]}>
                      Số loại bơ
                    </TableCell>
                    <TableCell align="right" sx={[oTieuDeCot, AN_DUOI_MD]}>
                      Tạm tính
                    </TableCell>
                    <TableCell align="right" sx={oTieuDeCot}>
                      Thao tác
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {dsNhap.map((n) => {
                    const tamTinh = n.items.reduce(
                      (s, it) => s + (Number(it.khoiLuong) || 0) * (Number(it.donGia) || 0),
                      0
                    );
                    return (
                      <TableRow
                        key={n.id}
                        sx={({ palette: mau }) => ({
                          transition: "background-color 160ms ease",
                          "&:hover": { backgroundColor: mau.grey[100] },
                        })}
                      >
                        <TableCell>
                          <MDTypography variant="button" fontWeight="medium" display="block">
                            {n.tenChuVuon ?? "—"}
                          </MDTypography>
                          <PhuNhap nhap={n} tamTinh={tamTinh} />
                        </TableCell>
                        <TableCell sx={AN_DUOI_MD}>{n.tenVuon ?? "—"}</TableCell>
                        <TableCell sx={AN_DUOI_MD}>
                          {n.maSotHienThi
                            ? `${n.maSotHienThi}${
                                n.soSotKhac > 0 ? ` +${n.soSotKhac} sọt khác` : ""
                              }`
                            : "—"}
                        </TableCell>
                        <TableCell sx={[AN_DUOI_MD, { whiteSpace: "nowrap" }]}>
                          {ngayGio(n.capNhatLuc)}
                        </TableCell>
                        <TableCell align="right" sx={AN_DUOI_MD}>
                          {n.items.length}
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={[AN_DUOI_MD, { whiteSpace: "nowrap" }]}
                        >{`${tamTinh.toLocaleString("vi-VN")} đ`}</TableCell>
                        <TableCell align="right">
                          <MDBox display="flex" flexWrap="wrap" justifyContent="flex-end" gap={0.5}>
                            <MDButton
                              component={Link}
                              to={`/thu-mua/${n.maChuVuon}/${n.maVuon}/nhap`}
                              variant="text"
                              color="info"
                              size="small"
                            >
                              Tiếp tục
                            </MDButton>
                            <MDButton
                              variant="text"
                              color="error"
                              size="small"
                              onClick={() => xoaNhap(n)}
                              disabled={dangXoaNhapId === n.id}
                            >
                              Xoá
                            </MDButton>
                          </MDBox>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </MDBox>
          </Card>
        )}

        {/* Ba sắc của "trái bơ bổ đôi" (vỏ / lá / hạt) làm mã màu cho 3 chỉ số
            — cùng bộ chỉ số với Trang chủ để hai màn hình đọc như một. */}
        <Grid id="tour-giao-dich-cua-toi-chi-so" container spacing={2.5} mb={3}>
          <Grid item xs={12} sm={4}>
            <TheChiSo
              nhan="Giao dịch"
              giaTri={danhSach.length}
              donVi="phiếu thu mua"
              icon="receipt_long"
              mau={palette.primary.main}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TheChiSo
              nhan="Khối lượng"
              giaTri={`${tongKg.toLocaleString("vi-VN")} kg`}
              donVi="đã cân trong ngày"
              icon="scale"
              mau={palette.info.main}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TheChiSo
              nhan="Thành tiền"
              giaTri={tongTien.toLocaleString("vi-VN")}
              donVi="đồng"
              icon="payments"
              mau={palette.gradients.avocado.main}
            />
          </Grid>
        </Grid>

        <Card id="tour-giao-dich-cua-toi-bang">
          <MDBox
            px={2.5}
            py={2}
            display="flex"
            flexWrap="wrap"
            alignItems="center"
            justifyContent="space-between"
            gap={2}
          >
            <MDTypography variant="h6" component="h2">
              Phiếu thu mua trong ngày
            </MDTypography>
            <MDTypography variant="caption" color="text">
              {danhSach.length} giao dịch
            </MDTypography>
          </MDBox>

          {dangTai ? (
            <MDBox px={2.5} pb={3}>
              <MDTypography variant="button" color="text">
                Đang tải...
              </MDTypography>
            </MDBox>
          ) : danhSach.length === 0 ? (
            <MDBox px={2.5} pb={4} pt={1} textAlign="center">
              <Icon sx={{ color: SEMANTIC.textDisabled, fontSize: "2.5rem" }}>receipt_long</Icon>
              <MDTypography variant="button" fontWeight="medium" display="block" mt={1}>
                Chưa có giao dịch trong ngày.
              </MDTypography>
              <MDTypography variant="caption" color="text" display="block" mt={0.5}>
                Đổi ngày ở đầu trang, hoặc bắt đầu một lượt thu mua mới.
              </MDTypography>
            </MDBox>
          ) : (
            <MDBox sx={{ overflowX: "auto" }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={oTieuDeCot}>Chủ vườn</TableCell>
                    <TableCell sx={[oTieuDeCot, AN_DUOI_MD]}>Giờ</TableCell>
                    <TableCell sx={[oTieuDeCot, AN_DUOI_MD]}>Vườn</TableCell>
                    <TableCell sx={[oTieuDeCot, AN_DUOI_MD]}>Loại bơ</TableCell>
                    <TableCell align="right" sx={[oTieuDeCot, AN_DUOI_MD]}>
                      Tổng kg
                    </TableCell>
                    <TableCell align="right" sx={oTieuDeCot}>
                      Thành tiền
                    </TableCell>
                    <TableCell align="right" sx={oTieuDeCot}>
                      TT
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {danhSach.map((po) => {
                    const tt = nhanTrangThaiThanhToan(po.trangThaiThanhToan);
                    return (
                      <TableRow
                        key={po.id}
                        hover
                        onClick={() => {
                          setLoiChuong("");
                          setThongBaoChuong("");
                          setXemChiTiet(po);
                        }}
                        sx={{ cursor: "pointer" }}
                      >
                        <TableCell>
                          <MDTypography variant="button" fontWeight="medium" display="block">
                            {po.tenChuVuon ?? "—"}
                          </MDTypography>
                          <PhuGiaoDich po={po} />
                        </TableCell>
                        <TableCell sx={[AN_DUOI_MD, { whiteSpace: "nowrap" }]}>
                          {gioPhut(po.thoiGian)}
                        </TableCell>
                        <TableCell sx={AN_DUOI_MD}>{po.tenVuon ?? "—"}</TableCell>
                        <TableCell sx={AN_DUOI_MD}>{po.tenLoaiBoGop || "—"}</TableCell>
                        <TableCell align="right" sx={[AN_DUOI_MD, { whiteSpace: "nowrap" }]}>
                          {po.tongKgPO}
                        </TableCell>
                        <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                          <MDTypography variant="button" fontWeight="medium">
                            {po.tongTienPO.toLocaleString("vi-VN")}
                          </MDTypography>
                        </TableCell>
                        <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                          <MDBadge
                            badgeContent={tt.label}
                            color={tt.color}
                            variant="gradient"
                            size="sm"
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </MDBox>
          )}
        </Card>
      </MDBox>
      <Footer />

      <Dialog
        open={!!xemChiTiet}
        onClose={() => setXemChiTiet(null)}
        fullScreen={fullManHinh}
        fullWidth
        maxWidth="md"
        PaperProps={oDialogPaperFormDai}
      >
        <DialogTitle component="div" sx={{ pb: 0.5, pr: 6, flexShrink: 0 }}>
          <NhanDau>Phiếu thu mua</NhanDau>
          <MDTypography variant="h5" component="h2">
            Chi tiết giao dịch
          </MDTypography>
          <IconButton
            onClick={() => setXemChiTiet(null)}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <Icon>close</Icon>
          </IconButton>
        </DialogTitle>
        {xemChiTiet && (
          <DialogContent sx={oDialogContentFormDai}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <MDTypography variant="caption" color="text" textTransform="uppercase">
                  Ngày giờ
                </MDTypography>
                <MDTypography variant="button" fontWeight="medium" display="block">
                  {new Date(xemChiTiet.thoiGian).toLocaleString("vi-VN")}
                </MDTypography>
              </Grid>
              <ThongTinChiTiet nhan="Chủ vườn" giaTri={xemChiTiet.tenChuVuon} xs={12} />
              <ThongTinChiTiet nhan="Vườn" giaTri={xemChiTiet.tenVuon} xs={12} />
              <Grid item xs={12}>
                <MDBox
                  pt={1.5}
                  sx={({ palette: mau }) => ({ borderTop: `1px solid ${mau.grey[300]}` })}
                >
                  <MDTypography variant="overline" color="text">
                    Chi tiết loại bơ
                  </MDTypography>
                </MDBox>
                <MDBox mt={0.5}>
                  {/* Nhóm theo LOẠI BƠ — 1 tiêu đề/loại bơ, kg×giá gộp gọn nếu
                      chỉ 1 sọt (đa số trường hợp); nếu loại bơ đó trải nhiều
                      sọt thì liệt kê từng sọt bên dưới thay vì lặp lại tên
                      loại bơ theo từng sọt như trước. */}
                  {nhomTheoLoaiBoXemChiTiet.map((nhom, iNhom) => {
                    const tongTienNhom = nhom.items.reduce((s, it) => s + it.thanhTien, 0);
                    return (
                      <MDBox
                        key={nhom.maLoaiBo}
                        py={0.75}
                        sx={({ palette: mau }) =>
                          iNhom > 0 ? { borderTop: `1px solid ${mau.grey[200]}` } : null
                        }
                      >
                        <MDBox
                          display="flex"
                          justifyContent="space-between"
                          alignItems="baseline"
                          gap={1}
                        >
                          <MDBox minWidth={0}>
                            <MDTypography variant="button" fontWeight="medium" display="block">
                              {nhom.tenLoaiBo ?? "—"}
                            </MDTypography>
                            {nhom.items.length === 1 && (
                              <MDTypography variant="caption" color="text">
                                {nhom.items[0].khoiLuong.toLocaleString("vi-VN")} kg ×{" "}
                                {nhom.items[0].donGia.toLocaleString("vi-VN")} đ/kg
                              </MDTypography>
                            )}
                          </MDBox>
                          <MDTypography
                            variant="button"
                            fontWeight="bold"
                            sx={{ whiteSpace: "nowrap" }}
                          >
                            {tongTienNhom.toLocaleString("vi-VN")} đ
                          </MDTypography>
                        </MDBox>
                        {nhom.items.length > 1 &&
                          nhom.items.map((it, idx) => (
                            <MDBox
                              key={idx}
                              display="flex"
                              justifyContent="space-between"
                              alignItems="flex-start"
                              gap={1}
                              mt={0.5}
                              pl={2}
                            >
                              <MDBox minWidth={0}>
                                {coNhieuSotXemChiTiet && it.maSotHienThi && (
                                  <MDTypography
                                    variant="caption"
                                    fontWeight="medium"
                                    color="info"
                                    display="block"
                                  >
                                    Sọt: {it.maSotHienThi}
                                  </MDTypography>
                                )}
                                <MDTypography variant="caption" color="text" display="block">
                                  {it.khoiLuong.toLocaleString("vi-VN")} kg ×{" "}
                                  {it.donGia.toLocaleString("vi-VN")} đ/kg
                                </MDTypography>
                              </MDBox>
                              <MDTypography
                                variant="caption"
                                color="text"
                                sx={{ whiteSpace: "nowrap" }}
                              >
                                {it.thanhTien.toLocaleString("vi-VN")} đ
                              </MDTypography>
                            </MDBox>
                          ))}
                      </MDBox>
                    );
                  })}
                </MDBox>
              </Grid>
              <ThongTinChiTiet
                nhan="Tổng thành tiền"
                giaTri={`${xemChiTiet.items
                  .reduce((s, it) => s + it.thanhTien, 0)
                  .toLocaleString("vi-VN")} đ`}
              />
              <Grid item xs={6}>
                <MDTypography variant="caption" color="text" textTransform="uppercase">
                  Hình thức
                </MDTypography>
                <MDBox mt={0.5}>
                  <MDBadge
                    badgeContent={tenHinhThucTt[xemChiTiet.hinhThucTt] ?? xemChiTiet.hinhThucTt}
                    color={mauHinhThucTt(xemChiTiet.hinhThucTt)}
                    variant="gradient"
                    size="sm"
                  />
                </MDBox>
              </Grid>
              <Grid item xs={6}>
                <MDTypography variant="caption" color="text" textTransform="uppercase">
                  Trạng thái thanh toán
                </MDTypography>
                <MDBox mt={0.5}>
                  <MDBadge
                    badgeContent={nhanTrangThaiThanhToan(xemChiTiet.trangThaiThanhToan).label}
                    color={nhanTrangThaiThanhToan(xemChiTiet.trangThaiThanhToan).color}
                    variant="gradient"
                    size="sm"
                  />
                </MDBox>
              </Grid>
              {xemChiTiet.trangThaiThanhToan === "da_thanh_toan" && (
                <Grid item xs={12}>
                  {xemChiTiet.thoiGianThanhToan && (
                    <MDTypography variant="caption" color="text" display="block" mb={1}>
                      Thanh toán lúc{" "}
                      {new Date(xemChiTiet.thoiGianThanhToan).toLocaleString("vi-VN")}
                    </MDTypography>
                  )}
                  <XemChungTuTrongApp url={xemChiTiet.chungTuUrl} />
                </Grid>
              )}
              {xemChiTiet.trangThaiThanhToan === "cho_xu_ly" && xemChiTiet.ghiChuThanhToan && (
                <Grid item xs={12}>
                  <MDTypography variant="caption" color="text" textTransform="uppercase">
                    Ghi chú kế toán trả lại
                  </MDTypography>
                  <MDTypography variant="button" display="block" color="warning" sx={chuXuongDong}>
                    {xemChiTiet.ghiChuThanhToan}
                  </MDTypography>
                </Grid>
              )}
              {xemChiTiet.trangThaiThanhToan === "cho_xu_ly" && (
                <Grid item xs={12}>
                  <MDBox display="flex" gap={1}>
                    <MDButton
                      component={Link}
                      to={`/thu-mua/xac-nhan/${xemChiTiet.id}`}
                      onClick={() => setXemChiTiet(null)}
                      variant="gradient"
                      color="info"
                      size="small"
                      fullWidth
                    >
                      Gửi về kế toán
                    </MDButton>
                    <MDButton
                      component={Link}
                      to={`/thu-mua/${xemChiTiet.maChuVuon}/${xemChiTiet.maVuon}/nhap`}
                      state={{ suaPOId: xemChiTiet.id }}
                      onClick={() => setXemChiTiet(null)}
                      variant="outlined"
                      color="secondary"
                      size="small"
                      fullWidth
                    >
                      Sửa
                    </MDButton>
                  </MDBox>
                </Grid>
              )}
              {xemChiTiet.trangThaiThanhToan === "cho_ke_toan" && (
                <Grid item xs={12}>
                  {loiChuong && (
                    <MDBox mb={1}>
                      <MDAlert color="error">{loiChuong}</MDAlert>
                    </MDBox>
                  )}
                  {thongBaoChuong && (
                    <MDBox mb={1}>
                      <MDAlert color="info">{thongBaoChuong}</MDAlert>
                    </MDBox>
                  )}
                  <MDButton
                    variant="gradient"
                    color="warning"
                    size="small"
                    fullWidth
                    onClick={() => chuongNhacKeToan(xemChiTiet.id)}
                    disabled={dangChuong}
                    startIcon={<Icon>notifications_active</Icon>}
                  >
                    {dangChuong ? "Đang chuông..." : "Chuông nhắc kế toán"}
                  </MDButton>
                  <MDTypography variant="caption" color="text" display="block" mt={1}>
                    Dùng khi đợi lâu mà kế toán chưa nhận. Cách nhau ít nhất 30 giây.
                  </MDTypography>
                </Grid>
              )}
              {xemChiTiet.ghiChu && (
                <Grid item xs={12}>
                  <MDTypography variant="caption" color="text" textTransform="uppercase">
                    Ghi chú
                  </MDTypography>
                  <MDTypography variant="button" display="block" sx={chuXuongDong}>
                    {xemChiTiet.ghiChu}
                  </MDTypography>
                </Grid>
              )}
            </Grid>
          </DialogContent>
        )}
      </Dialog>
    </DashboardLayout>
  );
}

export default GiaoDichCuaToi;
